import React from 'react';
import { APP_BG } from '@/constant/theme/colors.constant';

interface LoadingProps {
  fullScreen?: boolean;
  message?: string;
}

const Loading = ({ fullScreen = true, message }: LoadingProps) => (
  <div
    className={`flex items-center justify-center overflow-hidden
      ${APP_BG.base}
      ${fullScreen ? 'fixed inset-0 z-[9999]' : 'absolute inset-0'}`}
  >
    {/* Top accent bar */}
    <div
      className="absolute top-0 left-0 right-0 h-[3px]"
      style={{
        background: `linear-gradient(90deg, var(--color-primary), var(--color-secondary), var(--color-tertiary), var(--color-primary))`,
        backgroundSize: '300% 100%',
        animation: 'bar-sweep 2s linear infinite',
      }}
    />

    {/* Center spinner */}
    <div className="flex flex-col items-center gap-6">
      {/* Rings */}
      <div className="relative flex items-center justify-center w-16 h-16">
        {/* Outer ring */}
        <svg
          className="absolute w-16 h-16"
          style={{ animation: 'spin-cw 2.4s linear infinite' }}
          viewBox="0 0 64 64"
          fill="none"
        >
          <circle
            cx="32"
            cy="32"
            r="28"
            strokeWidth="2"
            stroke="url(#lg-outer)"
            strokeOpacity="0.25"
            strokeDasharray="140 36"
            strokeLinecap="round"
          />
          <defs>
            <linearGradient id="lg-outer" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="var(--color-primary)" />
              <stop offset="100%" stopColor="var(--color-tertiary)" />
            </linearGradient>
          </defs>
        </svg>

        {/* Inner ring */}
        <svg
          className="absolute w-10 h-10"
          style={{ animation: 'spin-ccw 1.6s linear infinite' }}
          viewBox="0 0 40 40"
          fill="none"
        >
          <circle
            cx="20"
            cy="20"
            r="16"
            strokeWidth="2.5"
            stroke="url(#lg-inner)"
            strokeDasharray="72 29"
            strokeLinecap="round"
          />
          <defs>
            <linearGradient id="lg-inner" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="var(--color-secondary)" />
              <stop offset="100%" stopColor="var(--color-primary)" />
            </linearGradient>
          </defs>
        </svg>

        {/* Center dot */}
        <div
          className="w-2.5 h-2.5 rounded-full"
          style={{
            background: `linear-gradient(135deg, var(--color-primary), var(--color-secondary))`,
            animation: 'dot-beat 1.2s ease-in-out infinite',
            boxShadow:
              '0 0 10px 3px color-mix(in srgb, var(--color-primary) 35%, transparent)',
          }}
        />
      </div>

      {/* Label + bouncing dots */}
      <div className="flex flex-col items-center gap-2">
        <span className="text-[10px] font-semibold tracking-[0.22em] uppercase text-gray-400 dark:text-gray-500">
          {message || 'Loading'}
        </span>
        <div className="flex gap-1">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="block w-1 h-1 rounded-full"
              style={{
                background: 'var(--color-primary)',
                animation: `dot-hop 1.1s ease-in-out ${i * 0.16}s infinite`,
              }}
            />
          ))}
        </div>
      </div>
    </div>

    <style>{`
      @keyframes spin-cw  { to { transform: rotate(360deg);  } }
      @keyframes spin-ccw { to { transform: rotate(-360deg); } }
      @keyframes bar-sweep {
        0%   { background-position: 0%   0; }
        100% { background-position: 300% 0; }
      }
      @keyframes dot-beat {
        0%, 100% { transform: scale(1);   }
        50%       { transform: scale(1.6); }
      }
      @keyframes dot-hop {
        0%, 80%, 100% { transform: translateY(0);    opacity: 0.4; }
        40%            { transform: translateY(-5px); opacity: 1;   }
      }
    `}</style>
  </div>
);

export default Loading;
